import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Form, Select, Table, Spin, Typography } from 'antd'
import { format } from 'date-fns'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import HighlighterWrapper from '../../HighlighterWrapper'
import { getReportByProject } from '../../../services/application'
import ProjectSelector from '../../ProjectSelector'
import exportToXLS from './exportToXLS'
import useTableFilter from '../../hooks/useTableFilter'
import { APP_STATUS_AGREE, APP_STATUS_DELIVERY, APP_STATUS_DONE, APP_STATUS_PAY, statuses } from '../constants'
import {
  calcImplSumAmount,
  priceNoVat,
  correctedPriceNoVat,
  priceSumNoVat,
  priceSumVat,
  sumNoVatByUnitWithCorrect,
  sumVatByUnitWithCorrect,
  calcSumAmount,
} from '../../../utils/calculation'
import { userSelector } from '../../../store/session'
import { getUserProjectNameById } from '../report-utils'
import { getOffBudget, OFF_BUDGET_OPTIONS } from '../../application/app-view-utils'
import { formatMoney, formatSum } from '../../../utils/number-utils'

const { Text } = Typography

const getCellStyling = (index, arr) => ({
  borderBottom: arr.length - 1 > index ? '1px solid #f0f0f0' : 0,
  padding: 8,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
})

const numberCellStyle = {
  padding: 8,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const defaultStatuses = [APP_STATUS_AGREE, APP_STATUS_PAY, APP_STATUS_DELIVERY, APP_STATUS_DONE]

const DEFAULT_HEIGHT = window.innerHeight || 600

const filterCategoryName = (filter) => (item) => {
  let { 'application_items.category': category } = filter
  return !category || item?.toString().toLocaleLowerCase().includes(category.toLowerCase())
}

const filterImplementationItem = (filter, estimate_items) => (item) => {
  let { 'application_items.category': category } = filter
  let eId = item?.application_item?.estimate_field?.id
  return (
    !category || isEmpty(estimate_items) || estimate_items.filter(filterEstimateItem(filter)).find((e) => eId === e.id)
  )
}

const filterApplicationItem = (filter) => (item) => {
  let { 'application_items.category': category } = filter
  let categoryName = item.category || item.estimate_field?.category
  return !category || categoryName?.toString().toLocaleLowerCase().includes(category.toLowerCase())
}

const filterEstimateItem = (filter) => (item) => {
  let { 'application_items.category': category } = filter
  return !category || item.category?.toString().toLocaleLowerCase().includes(category.toLowerCase())
}

const filterDataSource = (ds, filters) => {
  const filter = {
    'application_items.category': filters.estimate_name && filters.estimate_name[0],
  }
  return filters.estimate_name
    ? ds.map((item) => ({
        ...item,
        application_items: item.application_items.filter(filterApplicationItem(filter)),
        implementations: item.implementations.filter(filterImplementationItem(filter, item.estimate_items)),
        estimate_items: item.estimate_items.filter(filterEstimateItem(filter)),
      }))
    : ds
}

const buildAppItemMain = (container, application_item, item, findImplementation) => {
  const name = application_item.name ? application_item.name : 'no_name'
  const implementation = findImplementation(application_item.id).filter((impl) => !impl.isDetalisation)
  buildAppContainer(container, name, implementation, application_item, item.id, item.createdAt, false)
}

const buildAppItemDet = (container, application_item, item, findImplementation) => {
  const name = application_item.name ? application_item.name + ' (Детализация)' : 'no_name (Детализация)'
  const implementation = findImplementation(application_item.id).filter((impl) => impl.isDetalisation)
  buildAppContainer(container, name, implementation, application_item, item.id, item.createdAt, true)
}

const buildAppContainer = (
  container,
  name,
  implementation,
  application_item,
  itemId,
  itemCreatedAt,
  isDetalisation
) => {
  if (container[name]) {
    container[name]?.application_items?.push({
      application_id: itemId,
      application_date: itemCreatedAt,
      ...application_item,
    })
    if (implementation) {
      container[name]?.implementations?.push.apply(container[name]?.implementations, implementation)
    }
    const isEstimateExist = application_item?.estimate_field
      ? container[name].estimate_items.find((value) => application_item?.estimate_field?.id === value.id)
      : true
    if (!isEstimateExist) {
      container[name].estimate_items.push(application_item?.estimate_field)
    }
  } else {
    container[name] = {}
    container[name].isDetalisation = isDetalisation
    container[name].application_items = [
      { application_id: itemId, application_date: itemCreatedAt, ...application_item },
    ]
    container[name].implementations = implementation
    container[name].estimate_items = application_item?.estimate_field ? [application_item?.estimate_field] : []
    container[name].name = name
  }
}

const SummaryReport = () => {
  const [form] = Form.useForm()
  const [data, setData] = useState([])
  const [currentData, setCurrentData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [xScrollEnabled, setXScrollEnabled] = useState(false)
  const [project, setProject] = useState(null)

  const [filter, searchText, searchedColumn, getColumnSearchProps] = useTableFilter()

  const { user } = useSelector(userSelector)

  const columns = [
    {
      title: () => <span style={{ paddingLeft: 8 }}>Наименование заявка</span>,
      key: 'name',
      dataIndex: 'name',
      className: 'no-padding-cell-report',
      width: 250,
      ...getColumnSearchProps('name'),
      onFilter: (value, record) => get(record, 'name').toString().toLowerCase().includes(value.toLowerCase()),
      render: (_, record) => (
        <div style={{ padding: 8 }}>
          {searchedColumn === 'name' ? (
            <HighlighterWrapper
              searchWords={[searchText]}
              textToHighlight={get(record, 'name') ? get(record, 'name').toString() : ''}
            />
          ) : (
            get(record, 'name')
          )}
        </div>
      ),
    },
    {
      title: () => <span style={{ paddingLeft: 8 }}>Наименование Факт</span>,
      key: 'implementation_name',
      dataIndex: 'implementations',
      className: 'no-padding-cell-report',
      width: 250,
      ...getColumnSearchProps('implementations.name'),
      onFilter: (value, record) =>
        record.implementations.filter((item) => item?.name?.toString().toLowerCase().includes(value.toLowerCase()))
          .length > 0,
      render: (implementations, record) => {
        return (
          <div className="full-height-div">
            {implementations.filter(filterImplementationItem(filter, record.estimate_items)).map((item, index, arr) =>
              item.name ? (
                <div key={index} style={getCellStyling(index, arr)}>
                  {searchedColumn === 'implementations.name' ? (
                    <HighlighterWrapper searchWords={[searchText]} textToHighlight={item.name} />
                  ) : (
                    item.name
                  )}
                </div>
              ) : null
            )}
          </div>
        )
      },
    },
    {
      title: () => <span style={{ paddingLeft: 8 }}>Категория</span>,
      key: 'estimate_name',
      dataIndex: 'application_items',
      className: 'no-padding-cell-report',
      width: 250,
      ...getColumnSearchProps('application_items.category'),
      onFilter: (value, record) =>
        record.application_items
          .map((item) => item.category || item?.estimate_field?.category)
          .filter((category) => category?.toString().toLowerCase().includes(value.toLowerCase())).length > 0,
      render: (application_items) => (
        <div className="full-height-div">
          {application_items
            //.filter((item) => item.category || item?.estimate_field?.category)
            .map((item) => item.category || item?.estimate_field?.category)
            .reduce((acc, item) => {
              if (acc.indexOf(item) === -1) {
                acc.push(item)
              }
              return acc
            }, [])
            .filter(filterCategoryName(filter))
            .map((item, index, arr) => (
              <div key={index} style={getCellStyling(index, arr)}>
                {searchedColumn === 'application_items.category' ? (
                  <HighlighterWrapper searchWords={[searchText]} textToHighlight={item} />
                ) : (
                  item
                )}
              </div>
            ))}
        </div>
      ),
    },
    {
      title: () => <span style={{ paddingLeft: 8 }}>Вне Сметы</span>,
      key: 'off_budget',
      dataIndex: 'application_items',
      className: 'no-padding-cell-report',
      width: 250,
      ...getColumnSearchProps('application_items.off_budget', OFF_BUDGET_OPTIONS),
      onFilter: (value, record) => record.application_items.filter((item) => item?.off_budget === value).length > 0,
      render: (application_items) => (
        <div className="full-height-div">
          {application_items.filter(filterApplicationItem(filter)).map((item, index, arr) => {
            const offBudgetLabel = getOffBudget(item?.off_budget)
            return (
              <div key={index} style={getCellStyling(index, arr)}>
                {searchedColumn === 'application_items.off_budget' ? (
                  <HighlighterWrapper searchWords={[searchText]} textToHighlight={offBudgetLabel} />
                ) : (
                  offBudgetLabel
                )}
              </div>
            )
          })}
        </div>
      ),
    },
    {
      title: () => <span style={{ paddingLeft: 8 }}>Наименование Смета</span>,
      key: 'estimate_name',
      dataIndex: 'estimate_items',
      className: 'no-padding-cell-report',
      width: 250,
      ...getColumnSearchProps('estimate_items.name'),
      onFilter: (value, record) =>
        record.estimate_items.filter((item) => item?.name?.toString().toLowerCase().includes(value.toLowerCase()))
          .length > 0,
      render: (estimate_items) => (
        <div className="full-height-div">
          {estimate_items.filter(filterEstimateItem(filter)).map((item, index, arr) => (
            <div key={index} style={getCellStyling(index, arr)}>
              {searchedColumn === 'estimate_items.name' ? (
                <HighlighterWrapper searchWords={[searchText]} textToHighlight={item?.name} />
              ) : (
                item?.name
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Смета',
      children: [
        {
          title: () => <div style={{ padding: 8 }}>Кол-во</div>,
          key: 'estimate_count',
          dataIndex: 'estimate_items',
          className: 'no-padding-cell-report',
          render: (estimate_items, { isDetalisation }) => {
            return (
              <div className="full-height-div">
                {!isDetalisation &&
                  estimate_items.filter(filterEstimateItem(filter)).map((item, index, arr) =>
                    item?.amount ? (
                      <div key={index} style={{ ...getCellStyling(index, arr), ...numberCellStyle }}>
                        {formatSum(item.amount)}
                      </div>
                    ) : null
                  )}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Ед.изм.</div>,
          key: 'estimate_unit',
          dataIndex: 'estimate_items',
          className: 'no-padding-cell-report',
          render: (estimate_items, { isDetalisation }) => {
            return (
              <div className="full-height-div">
                {!isDetalisation &&
                  estimate_items.filter(filterEstimateItem(filter)).map((item, index, arr) =>
                    item?.unit ? (
                      <div key={index} style={getCellStyling(index, arr)}>
                        {item.unit}
                      </div>
                    ) : null
                  )}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Цена за ед, без НДС</div>,
          key: 'estimate_price',
          dataIndex: 'estimate_items',
          className: 'no-padding-cell-report',
          width: 170,
          render: (estimate_items, { isDetalisation }) => {
            return (
              <div className="full-height-div">
                {!isDetalisation &&
                  estimate_items.filter(filterEstimateItem(filter)).map((item, index, arr) =>
                    item?.price ? (
                      <div key={index} style={getCellStyling(index, arr)}>
                        {formatMoney(item.price)}
                      </div>
                    ) : null
                  )}
              </div>
            )
          },
        },
        {
          title: 'Сумма, без НДС',
          key: 'estimate_price',
          dataIndex: 'estimate_items',
          width: 150,
          render: (estimate_items, { isDetalisation }) => {
            if (!isDetalisation) {
              const value = estimate_items
                .filter(filterEstimateItem(filter))
                .reduce((acc, item) => acc + item.price * item.amount, 0)
              return formatMoney(value)
            }
          },
        },
      ],
    },
    {
      title: 'Заявка',
      children: [
        {
          title: () => <div style={{ padding: 8 }}>Заявка</div>,
          key: 'application_id',
          dataIndex: 'application_items',
          className: 'no-padding-cell-report',
          width: 90,
          render: (application_items) => (
            <div className="full-height-div">
              {application_items.filter(filterApplicationItem(filter)).map((item, index, arr) =>
                item?.application_id ? (
                  <div key={index} style={getCellStyling(index, arr)}>
                    <a href={`/applications?state=all&id=${item?.application_id}`} target="_blank" rel="noreferrer">
                      {item?.application_id}
                    </a>
                  </div>
                ) : null
              )}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Дата</div>,
          key: 'application_date',
          dataIndex: 'application_items',
          className: 'no-padding-cell-report',
          width: 90,
          render: (application_items) => (
            <div className="full-height-div">
              {application_items.filter(filterApplicationItem(filter)).map((item, index, arr) =>
                item?.application_date ? (
                  <div key={index} style={getCellStyling(index, arr)}>
                    {format(new Date(item?.application_date), 'dd-MM-yy')}
                  </div>
                ) : null
              )}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Кол-во</div>,
          key: 'application_count',
          dataIndex: 'application_items',
          className: 'no-padding-cell-report',
          render: (application_items) => (
            <div className="full-height-div">
              {application_items.filter(filterApplicationItem(filter)).map((item, index, arr) =>
                item?.amount ? (
                  <div key={index} style={{ ...getCellStyling(index, arr), ...numberCellStyle }}>
                    {formatSum(item.amount)}
                  </div>
                ) : null
              )}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Ед.изм.</div>,
          key: 'application_unit',
          dataIndex: 'application_items',
          className: 'no-padding-cell-report',
          render: (application_items) => (
            <div className="full-height-div">
              {application_items.filter(filterApplicationItem(filter)).map((item, index, arr) =>
                item?.unit ? (
                  <div key={index} style={getCellStyling(index, arr)}>
                    {item.unit}
                  </div>
                ) : null
              )}
            </div>
          ),
        },
      ],
    },
    {
      title: 'Факт',
      children: [
        {
          title: () => <div style={{ padding: 8 }}>Кол-во</div>,
          key: 'implementation_count',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations.filter(filterImplementationItem(filter, record.estimate_items)).map((item, index, arr) =>
                item?.amount ? (
                  <div key={index} style={{ ...getCellStyling(index, arr), ...numberCellStyle }}>
                    {formatSum(calcSumAmount(item))}
                  </div>
                ) : null
              )}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Ед.изм.</div>,
          key: 'implementation_unit',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations.filter(filterImplementationItem(filter, record.estimate_items)).map((item, index, arr) =>
                item?.unit ? (
                  <div key={index} style={getCellStyling(index, arr)}>
                    {item.unit}
                  </div>
                ) : null
              )}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Кор-ка кол-ва</div>,
          key: 'implementation_corrected_count',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations
                .filter(filterImplementationItem(filter, record.estimate_items))
                .map((item, index, arr) => (
                  <div key={index} style={{ ...getCellStyling(index, arr), ...numberCellStyle }}>
                    {formatSum(item?.correctedAmount)}
                  </div>
                ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Цена за ед. без НДС</div>,
          key: 'implementation_price',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 120,
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations
                .filter(filterImplementationItem(filter, record.estimate_items))
                .map((item, index, arr) => (
                  <div style={getCellStyling(index, arr)}>{formatMoney(priceNoVat(item))}</div>
                ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Кор-ка стоимости</div>,
          key: 'implementation_corrected_price',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 120,
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations
                .filter(filterImplementationItem(filter, record.estimate_items))
                .map((item, index, arr) => (
                  <div style={getCellStyling(index, arr)}>{formatMoney(correctedPriceNoVat(item))}</div>
                ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма по позиции без НДС</div>,
          key: 'implementation_position_sum',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 120,
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations
                .filter(filterImplementationItem(filter, record.estimate_items))
                .map((item, index, arr) => (
                  <div style={getCellStyling(index, arr)}>{formatMoney(priceSumNoVat(item))}</div>
                ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма, без НДС</div>,
          key: 'implementation_sum',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 140,
          render: (implementations, record) => {
            const sumObject = sumNoVatByUnitWithCorrect(
              implementations.filter(filterImplementationItem(filter, record.estimate_items)),
              record.estimate_items
            )
            return (
              <div className="full-height-div">
                {Object.values(sumObject).map((item, index, arr) =>
                  item.sum ? (
                    <div key={index} style={getCellStyling(index, arr)}>
                      {formatMoney(item.sum)}
                    </div>
                  ) : null
                )}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма с НДС</div>,
          key: 'implementation_sum_vat',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 140,
          render: (implementations, record) => {
            const sumObject = sumVatByUnitWithCorrect(
              implementations.filter(filterImplementationItem(filter, record.estimate_items)),
              record.estimate_items
            )
            return (
              <div className="full-height-div">
                {Object.values(sumObject).map((item, index, arr) =>
                  item.sum ? (
                    <div key={index} style={getCellStyling(index, arr)}>
                      {formatMoney(item.sum)}
                    </div>
                  ) : null
                )}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Всего кол-во</div>,
          key: 'implementation_amount_sum',
          dataIndex: 'implementations',
          render: (implementations, record) => (
            <div style={numberCellStyle}>
              {formatSum(
                calcImplSumAmount(implementations.filter(filterImplementationItem(filter, record.estimate_items)))
              )}
            </div>
          ),
        },
      ],
    },
    {
      title: 'экономия (-), перерасход (+)',
      children: [
        {
          title: () => <div style={{ padding: 8 }}>Кол-во</div>,
          key: 'savings_count',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 140,
          render: (implementations, record) => {
            const sumObject = implementations
              .filter(filterImplementationItem(filter, record.estimate_items))
              .reduce((acc, item) => {
                const estimateItem = record?.estimate_items?.find(
                  (estimate) => estimate.id === item.application_item?.estimate_field?.id
                )
                if (item.correctedAmount && estimateItem) {
                  if (!acc[estimateItem.unit]) {
                    acc[estimateItem.unit] = {}
                    acc[estimateItem.unit].sum = 0
                    acc[estimateItem.unit].unit = estimateItem.unit
                  }
                  acc[estimateItem.unit].sum += item.correctedAmount
                } else if (item.unit && item.amount) {
                  if (!acc[item.unit]) {
                    acc[item.unit] = {}
                    acc[item.unit].sum = 0
                    acc[item.unit].unit = item.unit
                  }
                  const finalAmount = calcSumAmount(item)
                  acc[item.unit].sum += finalAmount
                }
                return acc
              }, {})
            return (
              <div className="full-height-div">
                {Object.values(sumObject).map((item, index, arr) => {
                  const estimateAmount = record.estimate_items
                    .filter(filterEstimateItem(filter))
                    .filter((estimate) => estimate.unit === item.unit)
                    .reduce((acc, estimate) => acc + estimate.amount, 0)
                  const result = estimateAmount - item?.sum
                  return estimateAmount > 0 ? (
                    <div key={index} style={{ ...getCellStyling(index, arr), ...numberCellStyle }}>
                      {formatSum(result)}
                    </div>
                  ) : null
                })}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма, без НДС</div>,
          key: 'savings_sum',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 140,
          render: (implementations, record) => {
            const sumObject = sumNoVatByUnitWithCorrect(
              implementations.filter(filterImplementationItem(filter, record.estimate_items)),
              record.estimate_items
            )
            return (
              <div className="full-height-div">
                {Object.values(sumObject).map((item, index, arr) => {
                  const estimateItems = record.estimate_items
                  const estimateItem = estimateItems
                    .filter((estimate) => estimate.unit === item.unit)
                    .reduce((acc, estimate) => acc + estimate?.price * estimate?.amount, 0)
                  const result = estimateItem - item?.sum
                  return result ? (
                    <div key={index} style={getCellStyling(index, arr)}>
                      {formatMoney(result)}
                    </div>
                  ) : null
                })}
              </div>
            )
          },
        },
      ],
    },
  ]

  const onFormSubmit = (values) => {
    setIsLoading(true)

    const { project, statuses } = values

    setProject(project)

    getReportByProject(project, { statuses, implDetalisation: true })
      .then((response) => {
        const results = response?.data?.data?.attributes?.results
        if (results) {
          const keys = results.reduce((acc, item) => {
            const findImplementation = (appItemId) =>
              item?.implementation.implementation_items.filter((value) => value?.application_item?.id === appItemId)

            item?.application_items?.forEach((application_item) => {
              const hasDet = findImplementation(application_item.id).some((item) => item.isDetalisation)

              buildAppItemMain(acc, application_item, item, findImplementation)
              if (hasDet) buildAppItemDet(acc, application_item, item, findImplementation)
            })
            return acc
          }, {})
          setData(Object.values(keys))
          setCurrentData(Object.values(keys))
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      setXScrollEnabled(true)
    }
  }, [isLoading, data])

  return (
    <Spin spinning={isLoading}>
      <div style={{ paddingTop: 10 }}>
        <Form
          initialValues={{ statuses: defaultStatuses }}
          form={form}
          layout="vertical"
          style={{ display: 'flex' }}
          onFinish={onFormSubmit}
        >
          <Form.Item
            style={{ width: 300, maxWidth: 300 }}
            rules={[{ required: true, message: 'Выберите проект' }]}
            label="Проект"
            name="project"
          >
            <ProjectSelector />
          </Form.Item>
          <Form.Item style={{ marginLeft: 15 }} label="Статусы заявки" name="statuses">
            <Select mode="multiple" allowClear options={statuses} dropdownMatchSelectWidth={false} />
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button onClick={() => form.submit()}>Сформировать отчет</Button>
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button
              disabled={data?.length < 1}
              onClick={() => exportToXLS(currentData, getUserProjectNameById(user, project))}
            >
              Скачать
            </Button>
          </Form.Item>
        </Form>
        <Table
          id="summaryTable"
          columns={columns}
          dataSource={data}
          bordered
          size="small"
          pagination={false}
          scroll={{ x: xScrollEnabled ? 3300 : undefined, y: DEFAULT_HEIGHT - 240 }}
          onChange={(pagination, filters, sorter, extra) => {
            setCurrentData(filterDataSource(extra.currentDataSource, filters))
          }}
          summary={(pageData) => {
            const estimates = pageData.reduce((acc, { estimate_items }) => [...acc, ...estimate_items], [])
            const uniqueEstimates = estimates.filter((value, index) => {
              const _value = JSON.stringify(value)
              return (
                index ===
                estimates.findIndex((obj) => {
                  return JSON.stringify(obj) === _value
                })
              )
            })
            const estimateResult = uniqueEstimates
              .filter(filterEstimateItem(filter))
              .reduce((acc, item) => acc + item.price * item.amount, 0)
            const implementationResult = pageData.reduce((acc, { implementations }) => {
              return (
                acc +
                implementations
                  .filter(filterImplementationItem(filter, uniqueEstimates))
                  .reduce((implAcc, item) => implAcc + priceSumNoVat(item), 0)
              )
            }, 0)
            const implementationResultVat = pageData.reduce((acc, { implementations }) => {
              return (
                acc +
                implementations
                  .filter(filterImplementationItem(filter, uniqueEstimates))
                  .reduce((implAcc, item) => implAcc + priceSumVat(item), 0)
              )
            }, 0)

            const implCorrectedPriceRes = pageData.reduce((acc, { implementations }) => {
              return (
                acc +
                implementations
                  .filter(filterImplementationItem(filter, uniqueEstimates))
                  .reduce((implAcc, item) => implAcc + correctedPriceNoVat(item), 0)
              )
            }, 0)

            return (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Итого:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={7}></Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>
                    <Text strong>{formatMoney(estimateResult)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} colSpan={7}></Table.Summary.Cell>
                  <Table.Summary.Cell index={16}>
                    <Text strong>{formatMoney(implementationResult)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={17} colSpan={1}>
                    <Text strong>{formatMoney(implCorrectedPriceRes)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={19}>
                    <Text strong>{formatMoney(implementationResultVat)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )
          }}
        />
      </div>
    </Spin>
  )
}

export default SummaryReport
